/**
 * Modern SVG line icons (Lucide-style: 24x24 viewBox, 2px stroke, round caps,
 * no fill) — CLAUDE.md §4 "Icons: modern SVG line icons (Lucide-style). No
 * emoji in product UI." Hand-authored path data (no external icon dependency
 * added in this slice) rather than emoji or raster art.
 *
 * Add new icons here as the product grows; every icon is decorative by
 * default (`aria-hidden`) — callers supply the accessible name via a visible
 * label or `aria-label` on the control that hosts the icon.
 */
import type { SVGProps } from 'react'

export type IconName =
  | 'building2'
  | 'landmark'
  | 'briefcase'
  | 'activity'
  | 'bar-chart'
  | 'database'
  | 'settings'
  | 'home'
  | 'chevron-down'
  | 'chevron-up'
  | 'chevrons-up-down'
  | 'search'
  | 'filter'
  | 'columns'
  | 'bookmark'
  | 'download'
  | 'upload'
  | 'more-vertical'
  | 'info'
  | 'x'
  | 'plus'
  | 'check'
  | 'check-circle'
  | 'alert-circle'
  | 'alert-triangle'
  | 'minus-circle'
  | 'archive'
  | 'refresh-cw'
  | 'inbox'
  | 'rows'
  | 'chevron-right'
  | 'chevron-left'
  | 'users'
  | 'pin'
  | 'film'
  | 'lock'
  | 'unlock'
  | 'clock'
  | 'dollar-sign'
  | 'edit-3'
  | 'map-pin'
  | 'bell'
  | 'sliders'
  | 'mail'
  | 'copy'
  | 'printer'
  | 'trash-2'
  | 'star'
  | 'sun'
  | 'moon'
  | 'monitor'
  | 'log-out'
  | 'user'
  | 'sparkles'
  | 'user-plus'
  | 'user-x'
  | 'user-check'
  | 'shield'
  | 'zoom-in'
  | 'zoom-out'
  | 'maximize'
  | 'list-tree'
  | 'palette'
  | 'book-open'

const PATHS: Record<IconName, string> = {
  building2:
    'M6 21V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v17M14 21v-6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v6M3 21h18M9 7h1M9 11h1M9 15h1',
  landmark:
    'M3 21h18M4 21V9M20 21V9M3 9l9-6 9 6M8 9v12M12 9v12M16 9v12',
  briefcase:
    'M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8ZM8 6V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1M3 12h18',
  activity: 'M22 12h-4l-3 8-6-16-3 8H2',
  'bar-chart': 'M4 20V10M12 20V4M20 20v-6',
  database:
    'M4 6c0-1.66 3.58-3 8-3s8 1.34 8 3-3.58 3-8 3-8-1.34-8-3ZM4 6v12c0 1.66 3.58 3 8 3s8-1.34 8-3V6M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3',
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.56-1.11 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34h.05a1.7 1.7 0 0 0 1.02-1.56V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.56h.05a1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87v.05a1.7 1.7 0 0 0 1.56 1.02H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1.05Z',
  home: 'M3 11.5 12 4l9 7.5M5 10v10h14V10M9.5 20v-6h5v6',
  'chevron-down': 'M6 9l6 6 6-6',
  'chevron-up': 'M18 15l-6-6-6 6',
  'chevrons-up-down': 'M8 9l4-4 4 4M8 15l4 4 4-4',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.35-4.35',
  filter: 'M4 5h16l-6 8v6l-4 2v-8L4 5Z',
  columns: 'M4 4h16v16H4V4ZM9 4v16M15 4v16',
  bookmark: 'M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1Z',
  download: 'M12 3v12M7 10l5 5 5-5M4 21h16',
  upload: 'M12 21V9M7 14l5-5 5 5M4 21h16',
  'more-vertical': 'M12 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM12 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 11v6M12 7.5h.01',
  x: 'M18 6 6 18M6 6l12 12',
  plus: 'M12 5v14M5 12h14',
  check: 'M4 12l5 5L20 6',
  'check-circle': 'M21 11.08V12a9 9 0 1 1-5.34-8.24M22 4 12 14.01l-3-3',
  'alert-circle': 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 8v5M12 16h.01',
  'alert-triangle':
    'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0ZM12 9v4M12 17h.01',
  'minus-circle': 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 12h8',
  archive:
    'M3 6h18v3H3V6ZM4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9ZM10 13h4',
  'refresh-cw': 'M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15',
  inbox: 'M4 12h4l2 3h4l2-3h4M4 12 5.5 5A2 2 0 0 1 7.4 3.5h9.2A2 2 0 0 1 18.5 5L20 12v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-6Z',
  rows: 'M3 5h18v4H3V5ZM3 11h18v4H3v-4ZM3 17h18v2H3v-2Z',
  'chevron-right': 'M9 6l6 6-6 6',
  'chevron-left': 'M15 6l-6 6 6 6',
  users: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  pin: 'M12 17v5M8 3h8l1 6-9 4 9-4M7 9l9 4',
  film: 'M3 4h18v16H3V4ZM7 4v16M17 4v16M3 9h4M17 9h4M3 15h4M17 15h4',
  lock: 'M6 11V7a6 6 0 0 1 12 0v4M5 11h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z',
  unlock: 'M6 11V7a6 6 0 0 1 10.9-3.5M5 11h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v5l4 2',
  'dollar-sign': 'M12 2v20M17 5.5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  'edit-3': 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z',
  'map-pin':
    'M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0ZM12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  bell: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9ZM10.3 21a1.94 1.94 0 0 0 3.4 0',
  sliders: 'M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6',
  mail: 'M4 4h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1ZM3 6l9 7 9-7',
  copy: 'M9 9h11a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1ZM5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1v1',
  printer:
    'M6 9V2h12v7M4 18H2a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h20a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-2M6 14h12v8H6v-8Z',
  'trash-2':
    'M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3',
  star: 'M12 2l2.9 6.26L21 9.27l-4.5 4.24L17.8 20 12 16.9 6.2 20l1.3-6.49L3 9.27l6.1-1.01L12 2Z',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10ZM12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42',
  moon: 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z',
  monitor: 'M3 4h18v12H3V4ZM8 20h8M12 16v4',
  'log-out': 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  /** IB-172 (M16 AI Engines) — the "AI Assist" panel affordance. */
  sparkles:
    'M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3ZM5 17l.75 2.25L8 20l-2.25.75L5 23l-.75-2.25L2 20l2.25-.75L5 17ZM19 15l.6 1.8L21.4 17.4l-1.8.6L19 19.8l-.6-1.8-1.8-.6 1.8-.6L19 15Z',
  // user-admin-entra-federation Slice B — icons for the User Admin screen. `mail` already defined
  // above (HEAD path kept); only the net-new keys added here.
  'user-plus':
    'M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M8.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM19 8v6M22 11h-6',
  'user-x':
    'M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M8.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM17 8l5 5M22 8l-5 5',
  'user-check':
    'M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M8.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM16 11l2 2 4-4',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z',
  'zoom-in': 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.35-4.35M11 8v6M8 11h6',
  'zoom-out': 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.35-4.35M8 11h6',
  maximize: 'M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M3 16v3a2 2 0 0 0 2 2h3',
  'list-tree': 'M4 4h4v4H4V4ZM4 16h4v4H4v-4ZM8 6h4M8 18h4M12 6v12M12 12h8',
  /** IB-292 — the Dashboard colour theme picker's trigger (distinct from `settings`/`sliders`). */
  palette:
    'M12 22a1 1 0 0 1 0-20 10 9 0 0 1 10 9 5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8Z',
  /** In-app User Guide link (nav footer) — Lucide "book-open". */
  'book-open':
    'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2V3ZM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7V3Z',
}

export interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName
  size?: number
}

export function Icon({ name, size = 16, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d={PATHS[name]} />
    </svg>
  )
}
